<template>
  <div class="job-detail-page">
    <!-- 로딩 -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>로딩 중...</p>
    </div>

    <!-- 에러 -->
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <router-link to="/" class="btn-primary">홈으로 돌아가기</router-link>
    </div>

    <!-- 공고 내용 -->
    <template v-else-if="job">
      <!-- 썸네일 - 좌측 이미지 + 우측 정보 -->
      <div class="job-header">
        <div class="header-container">
          <div class="header-image">
            <img 
              :src="job.thumbnail || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop'" 
              :alt="job.siteName"
              @error="handleImageError"
            />
          </div>

          <div class="header-content">
            <div class="badge-group">
              <span v-if="job.isPremium" class="badge premium">PREMIUM</span>
              <span v-if="job.isUrgent" class="badge urgent">급구</span>
              <span v-if="job.isNew" class="badge new">NEW</span>
              <span v-if="dDay !== null" class="badge" :class="dDayClass">{{ dDayText }}</span>
            </div>
            
            <h1 class="job-title">{{ job.title }}</h1>
            
            <div class="job-meta">
              <span class="meta-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                {{ job.siteName }}
              </span>
              <span class="meta-item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                {{ formatDate(job.startDate) }}
              </span>
            </div>

            <!-- 주요 정보 -->
            <div class="quick-info">
              <div class="quick-info-item">
                <span class="label">지역</span>
                <span class="value">{{ job.region }}</span>
              </div>
              <div class="quick-info-item">
                <span class="label">직종</span>
                <span class="value">{{ job.jobType }}</span>
              </div>
              <div class="quick-info-item highlight">
                <span class="label">급여</span>
                <span class="value">{{ job.salaryType }} {{ job.salary }}</span>
              </div>
            </div>

            <!-- 액션 버튼 -->
            <div class="header-actions">
              <a :href="`tel:${job.contactPhone}`" class="btn-primary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                전화 문의
              </a>
              <button 
                class="btn-bookmark"
                :class="{ active: isBookmarkedJob }"
                @click="handleBookmark"
                :disabled="!user"
                :title="!user ? '로그인이 필요합니다' : (isBookmarkedJob ? '관심 공고 해제' : '관심 공고 등록')"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" :fill="isBookmarkedJob ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {{ isBookmarkedJob ? '관심 해제' : '관심 등록' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="job-content">
        <div class="content-container">
          <!-- 좌측 메인 컨텐츠 -->
          <div class="content-main">
            <!-- 현장 정보 -->
            <section class="info-card">
              <h2 class="card-title">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                현장 정보
              </h2>
              <div class="info-grid">
                <div class="info-row">
                  <span class="label">현장명</span>
                  <span class="value">{{ job.siteName }}</span>
                </div>
                <div class="info-row">
                  <span class="label">투입일</span>
                  <span class="value">{{ formatDate(job.startDate) }}</span>
                </div>
                <div class="info-row" v-if="job.deadline">
                  <span class="label">마감일</span>
                  <span class="value">{{ formatDate(job.deadline) }}</span>
                </div>
                <div class="info-row full">
                  <span class="label">현장 주소</span>
                  <span class="value">{{ job.address }}</span>
                </div>
                <div class="info-row" v-if="job.constructor">
                  <span class="label">시공사</span>
                  <span class="value">{{ job.constructor }}</span>
                </div>
                <div class="info-row" v-if="job.developer">
                  <span class="label">시행사</span>
                  <span class="value">{{ job.developer }}</span>
                </div>
                <div class="info-row" v-if="job.agency">
                  <span class="label">대행사</span>
                  <span class="value">{{ job.agency }}</span>
                </div>
                <div class="info-row" v-if="job.trust">
                  <span class="label">신탁사</span>
                  <span class="value">{{ job.trust }}</span>
                </div>
              </div>

              <!-- 카카오 지도 -->
              <div class="map-wrapper">
                <h3 class="map-title">현장 위치</h3>
                <KakaoMap 
                  :address="job.address" 
                  :marker-title="job.siteName"
                  height="400px"
                />
              </div>
            </section>

            <!-- 모집 조건 -->
            <section class="info-card">
              <h2 class="card-title">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                모집 조건
              </h2>
              <div class="info-grid">
                <div class="info-row">
                  <span class="label">직종</span>
                  <span class="value">{{ job.jobType }}</span>
                </div>
                <div class="info-row" v-if="job.headcount">
                  <span class="label">모집인원</span>
                  <span class="value">{{ job.headcount }}</span>
                </div>
                <div class="info-row">
                  <span class="label">성별</span>
                  <span class="value">{{ job.gender || '무관' }}</span>
                </div>
                <div class="info-row" v-if="job.age">
                  <span class="label">나이</span>
                  <span class="value">{{ job.age }}</span>
                </div>
                <div class="info-row">
                  <span class="label">경력</span>
                  <span class="value">{{ job.experience || '무관' }}</span>
                </div>
              </div>
            </section>

            <!-- 급여 정보 -->
            <section class="info-card">
              <h2 class="card-title">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
                급여 정보
              </h2>
              <div class="info-grid">
                <div class="info-row">
                  <span class="label">급여 형태</span>
                  <span class="value">{{ job.salaryType }}</span>
                </div>
                <div class="info-row">
                  <span class="label">급여</span>
                  <span class="value salary">{{ job.salary }}</span>
                </div>
                <div class="info-row" v-if="job.transportationFee">
                  <span class="label">교통비</span>
                  <span class="value">{{ job.transportationFee }}</span>
                </div>
                <div class="info-row" v-if="job.salesFee">
                  <span class="label">영업비</span>
                  <span class="value">{{ job.salesFee }}</span>
                </div>
                <div class="info-row full" v-if="job.benefits">
                  <span class="label">근무 후생</span>
                  <span class="value">{{ job.benefits }}</span>
                </div>
              </div>
            </section>

            <!-- 상세 내용 -->
            <section class="info-card" v-if="job.description">
              <h2 class="card-title">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                상세 내용
              </h2>
              <div class="description">
                {{ job.description }}
              </div>
            </section>
          </div>

          <!-- 우측 사이드바 -->
          <aside class="content-sidebar">
            <!-- 담당자 정보 -->
            <div class="sidebar-card sticky">
              <h3 class="sidebar-title">담당자 정보</h3>
              <div class="contact-info">
                <div class="contact-item">
                  <span class="contact-label">담당자</span>
                  <span class="contact-value">{{ job.contactName }}</span>
                </div>
                <div class="contact-item">
                  <span class="contact-label">연락처</span>
                  <a :href="`tel:${job.contactPhone}`" class="contact-value phone">
                    {{ job.contactPhone }}
                  </a>
                </div>
              </div>

              <!-- 관심 등록 버튼 (사이드바) -->
              <button 
                class="btn-sidebar-bookmark"
                :class="{ active: isBookmarkedJob }"
                @click="handleBookmark"
                :disabled="!user"
                :title="!user ? '로그인이 필요합니다' : (isBookmarkedJob ? '관심 공고 해제' : '관심 공고 등록')"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" :fill="isBookmarkedJob ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {{ isBookmarkedJob ? '관심 공고 해제' : '관심 공고 등록' }}
              </button>

              <!-- 공유하기 버튼 -->
              <button class="btn-share" @click="handleShare">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="18" cy="5" r="3"/>
                  <circle cx="6" cy="12" r="3"/>
                  <circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                공유하기
              </button>

              <div class="sidebar-links" v-if="job.siteUrl || job.snsUrl">
                <a v-if="job.siteUrl" :href="job.siteUrl" target="_blank" rel="noopener noreferrer" class="link-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  웹사이트
                </a>
                <a v-if="job.snsUrl" :href="job.snsUrl" target="_blank" rel="noopener noreferrer" class="link-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                  SNS
                </a>
              </div>

              <div class="sidebar-stats">
                <span class="stat-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  {{ job.views || 0 }}
                </span>
                <span class="stat-item">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  {{ job.bookmarkCount || 0 }}
                </span>
              </div>

              <div class="sidebar-date">
                등록일: {{ formatDate(job.createdAt) }}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <!-- 유사 공고 섹션 -->
      <section class="similar-jobs" v-if="similarJobs.length > 0">
        <div class="similar-container">
          <h2 class="similar-title">공고 추천</h2>
          <p class="similar-subtitle">{{ job.region }} 지역 또는 {{ job.jobType }} 직종의 다른 공고입니다</p>
          
          <div class="similar-grid">
            <router-link 
              v-for="similarJob in similarJobs" 
              :key="similarJob.id"
              :to="`/jobs/${similarJob.id}`"
              class="similar-card"
            >
              <div class="similar-image">
                <img :src="similarJob.thumbnail || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=300&fit=crop'" :alt="similarJob.siteName" />
                <div class="similar-badges">
                  <span v-if="similarJob.isPremium" class="badge-small premium">PREMIUM</span>
                  <span v-if="similarJob.isUrgent" class="badge-small urgent">급구</span>
                </div>
              </div>
              <div class="similar-content">
                <h3 class="similar-job-title">{{ similarJob.title }}</h3>
                <div class="similar-info">
                  <span class="info-badge">{{ similarJob.region }}</span>
                  <span class="info-badge">{{ similarJob.jobType }}</span>
                </div>
                <div class="similar-salary">{{ similarJob.salaryType }} {{ similarJob.salary }}</div>
              </div>
            </router-link>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useJobs } from '@/composables/useJobs'
import { useBookmarks } from '@/composables/useBookmarks'
import KakaoMap from '@/components/KakaoMap.vue'

export default {
  name: 'JobDetailView',
  components: {
    KakaoMap
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { user, userProfile } = useAuth()
    const { getJob, getJobs, incrementViews, loading, error } = useJobs()
    const { toggleBookmark, isBookmarked } = useBookmarks()
    
    const job = ref(null)
    const similarJobs = ref([])

    const isBookmarkedJob = computed(() => {
      if (!job.value || !userProfile.value) return false
      return isBookmarked(userProfile.value, job.value.id)
    })

    // D-Day 계산
    const dDay = computed(() => {
      if (!job.value?.deadline) return null
      
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      const deadlineDate = job.value.deadline.toDate ? job.value.deadline.toDate() : new Date(job.value.deadline)
      deadlineDate.setHours(0, 0, 0, 0)
      
      const diffTime = deadlineDate - today
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      return diffDays
    })

    const dDayText = computed(() => {
      if (dDay.value === null) return ''
      if (dDay.value === 0) return 'D-DAY'
      if (dDay.value < 0) return '마감'
      return `D-${dDay.value}`
    })

    const dDayClass = computed(() => {
      if (dDay.value === null) return ''
      if (dDay.value === 0) return 'dday'
      if (dDay.value < 0) return 'closed'
      if (dDay.value <= 3) return 'urgent'
      if (dDay.value <= 7) return 'warning'
      return 'normal'
    })

    const formatDate = (date) => {
      if (!date) return '-'
      if (date.toDate) {
        date = date.toDate()
      }
      return new Date(date).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const handleImageError = (e) => {
      e.target.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop'
    }

    const handleBookmark = async () => {
      if (!user.value) {
        if (confirm('로그인이 필요합니다. 로그인 페이지로 이동하시겠습니까?')) {
          router.push('/login')
        }
        return
      }

      try {
        await toggleBookmark(user.value.uid, job.value.id, isBookmarkedJob.value)
        job.value.bookmarkCount = (job.value.bookmarkCount || 0) + (isBookmarkedJob.value ? -1 : 1)
        if (isBookmarkedJob.value) {
          userProfile.value.bookmarks = userProfile.value.bookmarks.filter(id => id !== job.value.id)
        } else {
          userProfile.value.bookmarks.push(job.value.id)
        }
      } catch (e) {
        console.error('북마크 처리 실패:', e)
        alert('북마크 처리에 실패했습니다.')
      }
    }

    // 공유하기
    const handleShare = async () => {
      const shareData = {
        title: job.value.title,
        text: `${job.value.siteName} - ${job.value.jobType} 채용`,
        url: window.location.href
      }

      try {
        if (navigator.share) {
          await navigator.share(shareData)
        } else {
          // Fallback: 클립보드에 복사
          await navigator.clipboard.writeText(window.location.href)
          alert('링크가 클립보드에 복사되었습니다!')
        }
      } catch (err) {
        console.error('공유 실패:', err)
      }
    }

    // 유사 공고 로드
    const loadSimilarJobs = async () => {
      try {
        const allJobs = await getJobs() // getAllJobs → getJobs
        
        // 현재 공고 제외, 같은 지역 또는 같은 직종
        const filtered = allJobs
          .filter(j => j.id !== job.value.id && (j.region === job.value.region || j.jobType === job.value.jobType))
          .slice(0, 6)
        
        similarJobs.value = filtered
      } catch (e) {
        console.error('유사 공고 로드 실패:', e)
      }
    }

    onMounted(async () => {
      const jobId = route.params.id
      job.value = await getJob(jobId)
      
      if (job.value) {
        incrementViews(jobId, job.value.views || 0)
        loadSimilarJobs()
      }
    })

    return {
      job,
      user,
      userProfile,
      loading,
      error,
      isBookmarkedJob,
      dDay,
      dDayText,
      dDayClass,
      similarJobs,
      formatDate,
      handleImageError,
      handleBookmark,
      handleShare
    }
  }
}
</script>

<style scoped>
.job-detail-page {
  min-height: calc(100vh - 80px);
  background: #f8f9fa;
}

/* 로딩 & 에러 */
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 500px;
  gap: 20px;
  color: #6b7684;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f2f4f6;
  border-top-color: #3182f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 헤더 - 좌측 이미지 + 우측 정보 */
.job-header {
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}

.header-container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 60px 40px;
  display: grid;
  grid-template-columns: 480px 1fr;
  gap: 60px;
  align-items: center;
}

.header-image {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  background: #f8f9fa;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
}

.header-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.badge-group {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 700;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge.premium {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.badge.urgent {
  background: #e03131;
  color: #fff;
}

.badge.new {
  background: #12b886;
  color: #fff;
}

.badge.dday {
  background: #e03131;
  color: #fff;
  animation: pulse 1.5s infinite;
}

.badge.closed {
  background: #868e96;
  color: #fff;
}

.badge.warning {
  background: #fd7e14;
  color: #fff;
}

.badge.normal {
  background: #3182f6;
  color: #fff;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.job-title {
  font-size: 42px;
  font-weight: 800;
  color: #191f28;
  line-height: 1.2;
  letter-spacing: -0.5px;
}

.job-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #4e5968;
  font-weight: 500;
}

.meta-item svg {
  color: #8b95a1;
}

.quick-info {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 12px;
}

.quick-info-item {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.quick-info-item.highlight {
  background: linear-gradient(135deg, #e7f5ff 0%, #d0ebff 100%);
  border-color: #74c0fc;
}

.quick-info-item .label {
  display: block;
  font-size: 13px;
  color: #6b7684;
  font-weight: 600;
  margin-bottom: 8px;
}

.quick-info-item .value {
  display: block;
  font-size: 18px;
  color: #191f28;
  font-weight: 700;
}

.quick-info-item.highlight .value {
  color: #1971c2;
}

.header-actions {
  display: flex;
  gap: 12px;
  margin-top: 12px;
}

.btn-primary {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 32px;
  background: linear-gradient(135deg, #3182f6 0%, #1b64da 100%);
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 14px rgba(49, 130, 246, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(49, 130, 246, 0.4);
}

.btn-bookmark {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px 32px;
  background: #fff;
  color: #4e5968;
  font-size: 16px;
  font-weight: 600;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-bookmark:hover {
  border-color: #e03131;
  color: #e03131;
  background: #fff5f5;
}

.btn-bookmark.active {
  background: #fff5f5;
  border-color: #e03131;
  color: #e03131;
}

.btn-bookmark:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 컨텐츠 영역 */
.job-content {
  background: #f8f9fa;
}

.content-container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 40px 40px 80px;
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 32px;
}

/* 카드 스타일 */
.info-card {
  background: #fff;
  padding: 36px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 22px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f1f3f5;
}

.card-title svg {
  color: #3182f6;
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  background: #f8f9fa;
  border-radius: 10px;
}

.info-row.full {
  grid-column: span 2;
}

.info-row .label {
  font-size: 13px;
  color: #6b7684;
  font-weight: 600;
}

.info-row .value {
  font-size: 16px;
  color: #191f28;
  font-weight: 600;
}

.info-row .value.salary {
  color: #1971c2;
  font-size: 20px;
  font-weight: 700;
}

.description {
  font-size: 16px;
  line-height: 1.8;
  color: #495057;
  white-space: pre-wrap;
  padding: 24px;
  background: #f8f9fa;
  border-radius: 12px;
  border-left: 4px solid #3182f6;
}

/* 지도 */
.map-wrapper {
  margin-top: 32px;
  padding-top: 32px;
  border-top: 2px solid #f1f3f5;
}

.map-title {
  font-size: 18px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 16px;
}

/* 사이드바 */
.sidebar-card {
  background: #fff;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.sidebar-card.sticky {
  position: sticky;
  top: 120px;
}

.sidebar-title {
  font-size: 20px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 24px;
}

.contact-info {
  margin-bottom: 20px;
}

.contact-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #f1f3f5;
}

.contact-item:last-child {
  border-bottom: none;
}

.contact-label {
  font-size: 14px;
  color: #6b7684;
  font-weight: 600;
}

.contact-value {
  font-size: 16px;
  font-weight: 700;
  color: #191f28;
}

.contact-value.phone {
  color: #3182f6;
  transition: color 0.2s;
}

.contact-value.phone:hover {
  color: #1b64da;
}

/* 사이드바 버튼들 */
.btn-sidebar-bookmark,
.btn-share {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 12px;
}

.btn-sidebar-bookmark {
  background: #fff;
  color: #4e5968;
  border: 2px solid #e9ecef;
}

.btn-sidebar-bookmark:hover {
  border-color: #e03131;
  color: #e03131;
  background: #fff5f5;
}

.btn-sidebar-bookmark.active {
  background: #fff5f5;
  border-color: #e03131;
  color: #e03131;
}

.btn-sidebar-bookmark:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-share {
  background: linear-gradient(135deg, #12b886 0%, #0ca678 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 8px rgba(18, 184, 134, 0.3);
}

.btn-share:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(18, 184, 134, 0.4);
}

.sidebar-links {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.link-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  background: #f8f9fa;
  color: #495057;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  transition: all 0.2s;
}

.link-btn:hover {
  background: #3182f6;
  color: #fff;
}

.sidebar-stats {
  display: flex;
  justify-content: space-around;
  padding: 20px 0;
  border-top: 1px solid #f1f3f5;
  border-bottom: 1px solid #f1f3f5;
  margin-bottom: 16px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #6b7684;
  font-weight: 600;
}

.sidebar-date {
  text-align: center;
  font-size: 14px;
  color: #8b95a1;
}

/* 유사 공고 섹션 */
.similar-jobs {
  background: #fff;
  padding: 80px 40px;
  border-top: 1px solid #e9ecef;
}

.similar-container {
  max-width: 1600px;
  margin: 0 auto;
}

.similar-title {
  font-size: 32px;
  font-weight: 800;
  color: #191f28;
  margin-bottom: 12px;
  text-align: center;
}

.similar-subtitle {
  font-size: 16px;
  color: #6b7684;
  text-align: center;
  margin-bottom: 48px;
}

.similar-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}

.similar-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.3s;
  border: 1px solid #e9ecef;
}

.similar-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.similar-image {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #f8f9fa;
}

.similar-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.similar-badges {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 6px;
}

.badge-small {
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-small.premium {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.badge-small.urgent {
  background: #e03131;
  color: #fff;
}

.similar-content {
  padding: 24px;
}

.similar-job-title {
  font-size: 18px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 12px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.similar-info {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.info-badge {
  padding: 6px 12px;
  background: #f8f9fa;
  color: #495057;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
}

.similar-salary {
  font-size: 16px;
  font-weight: 700;
  color: #1971c2;
}

/* 반응형 - 태블릿 (1200px 이하) */
@media (max-width: 1200px) {
  .header-container {
    max-width: 100%;
    padding: 40px 24px;
    grid-template-columns: 400px 1fr;
    gap: 40px;
  }

  .content-container {
    max-width: 100%;
    padding: 32px 24px 60px;
  }

  .job-title {
    font-size: 34px;
  }

  .quick-info {
    grid-template-columns: 1fr 1fr;
  }

  .quick-info-item:last-child {
    grid-column: span 2;
  }

  .similar-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* 반응형 - 모바일 (768px 이하) */
@media (max-width: 768px) {
  .header-container {
    grid-template-columns: 1fr;
    padding: 32px 20px;
    gap: 32px;
  }

  .job-title {
    font-size: 26px;
  }

  .meta-item {
    font-size: 14px;
  }

  .quick-info {
    grid-template-columns: 1fr;
  }

  .quick-info-item:last-child {
    grid-column: span 1;
  }

  .header-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-bookmark {
    width: 100%;
    padding: 16px;
    font-size: 15px;
  }

  .content-container {
    grid-template-columns: 1fr;
    padding: 24px 20px 60px;
    gap: 24px;
  }

  .info-card {
    padding: 24px 20px;
  }

  .card-title {
    font-size: 18px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .info-row.full {
    grid-column: span 1;
  }

  .sidebar-card {
    position: static;
    padding: 24px 20px;
  }

  .badge {
    padding: 6px 12px;
    font-size: 11px;
  }

  .similar-jobs {
    padding: 60px 20px;
  }

  .similar-title {
    font-size: 26px;
  }

  .similar-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}

/* 반응형 - 작은 모바일 (480px 이하) */
@media (max-width: 480px) {
  .header-container {
    padding: 24px 16px;
    gap: 24px;
  }

  .job-title {
    font-size: 22px;
  }

  .quick-info-item {
    padding: 16px;
  }

  .quick-info-item .value {
    font-size: 16px;
  }

  .btn-primary,
  .btn-bookmark {
    padding: 14px;
    font-size: 14px;
  }

  .content-container {
    padding: 20px 16px 50px;
  }

  .info-card {
    padding: 20px 16px;
  }

  .info-row {
    padding: 14px;
  }

  .sidebar-card {
    padding: 20px 16px;
  }

  .similar-jobs {
    padding: 50px 16px;
  }

  .similar-title {
    font-size: 22px;
  }
}
</style>